/**
 * Session and account endpoints — screens A3/A4/A5 and F1/F2/F3/F7.
 *
 * One rule runs through the whole file: the refresh token is never passed around by
 * hand. It lives in the HttpOnly cookie, and every handler that needs to know "which
 * session is this" reads it from there (`resolve_acting_family` in
 * crates/api/src/routes/consumer/me.rs, `logout` in routes/auth.rs). Passing it in a
 * body would mean JS could read it, which is the whole thing we are avoiding.
 */
import { apiRequest, setTokens, type LoginResponse } from '@/shared/api/client';
import { deviceToken, rememberDeviceToken } from './deviceToken';
import { markSignedOut } from './passkey';

export interface ConsumerProfile {
  id: string;
  /** For consumers `users.username` IS the lower-cased registration email — one field, both roles. */
  email: string;
  created_at: string;
  /** True while an account-deletion request is still awaiting the back-office (F7). */
  deletion_request_pending: boolean;
  /** Адрес подтверждён кодом из письма. У аккаунтов старше подтверждения — `false`. */
  email_verified: boolean;
  /**
   * Письма на этот адрес не доставляются — есть хотя бы одно, исчерпавшее все попытки.
   *
   * Сильнее «не подтверждён»: это факт со стороны почтовых серверов, а не отсутствие действия
   * человека. Именно на него вешается предупреждение про чек.
   */
  email_delivery_failed: boolean;
}

/**
 * The wire field is `username`, not `email` — the backend shares this DTO across roles,
 * and expects the address lower-cased (registration stores it that way).
 */
/** Картинка с кодом: сервер просит её после пяти неудач по паре «логин + адрес». */
export interface CaptchaChallenge {
  id: string;
  /** PNG, base64 — показывать как `data:image/png;base64,…`. */
  image_png_base64: string;
  ttl_seconds: number;
}

export interface CaptchaAnswer {
  id: string;
  answer: string;
}

export function fetchCaptcha(): Promise<CaptchaChallenge> {
  return apiRequest<CaptchaChallenge>('/v1/auth/captcha', { method: 'GET' });
}

export async function login(
  email: string,
  password: string,
  captcha?: CaptchaAnswer | null,
): Promise<LoginResponse> {
  // Секрет доверенного устройства: с ним перебор чужого пароля с сотен адресов не запирает
  // владельца, вошедшего со своего телефона. Пароль он не заменяет — см. `deviceToken.ts`.
  // Капча — только когда сервер её попросил (`captcha_required`): сосед за тем же NAT, пять
  // раз ошибившийся паролем, больше не запирает нашу пару «логин + адрес».
  const data = await apiRequest<LoginResponse>('/v1/auth/consumer/login', {
    method: 'POST',
    body: {
      username: email.trim().toLowerCase(),
      password,
      device_token: deviceToken(),
      ...(captcha ? { captcha } : {}),
    },
  });
  setTokens(data.access_token, data.refresh_token);
  rememberDeviceToken(data.device_token);
  return data;
}

export interface RegisterInput {
  email: string;
  password: string;
  /** «Принимаю оферту и политику конфиденциальности» — its own versioned `user_consents` row. */
  accepted_offer_and_privacy: boolean;
  /** «Согласие на обработку персональных данных» — 152-ФЗ requires this one separately. */
  accepted_personal_data_processing: boolean;
  /** «Подтверждаю чек-лист перед заказом» — its own versioned row, and its own gate. */
  accepted_order_checklist: boolean;
  /**
   * The redactions the form actually showed under the checkboxes.
   *
   * Without them the tick proves only that a button was pressed, not what it was pressed under: the
   * server stamps `document_id` from its own lookup, and a redaction published between opening the
   * form and submitting it would be recorded as the one that was read. The window is seconds wide
   * and it only has to happen once for the record to stop being evidence. Mismatch answers
   * `legal_documents_changed` and the form re-reads the list.
   */
  accepted_documents: Array<{ kind: string; version: string; content_hash: string }>;
  /**
   * Пропуск, выданный за верный код из письма (`confirmEmailVerification`).
   *
   * Необязательный на проводе — это фаза выката на сервере, а не необязательность по существу:
   * пока `REQUIRE_EMAIL_VERIFICATION` выключен, регистрация без него проходит, но адрес остаётся
   * неподтверждённым. Этот клиент всегда присылает билет — экран регистрации без него до
   * последнего шага просто не доходит.
   */
  verification_ticket?: string;
}

/**
 * Registration returns tokens directly, so a new account lands signed in — the same
 * contract the mobile clients use. The three consents are deliberately separate fields:
 * they are stored as separate versioned rows and merging them into one checkbox would
 * make the 152-ФЗ consent legally unprovable.
 */
export async function register(input: RegisterInput): Promise<LoginResponse> {
  const data = await apiRequest<LoginResponse>('/v1/auth/consumer/register', {
    method: 'POST',
    body: { ...input, email: input.email.trim().toLowerCase() },
  });
  setTokens(data.access_token, data.refresh_token);
  rememberDeviceToken(data.device_token);
  return data;
}

export async function logout(): Promise<void> {
  // Сам вышел — экран входа в этой вкладке Face ID сам не открывает (см. `markSignedOut`).
  markSignedOut();
  try {
    await apiRequest<void>('/v1/auth/logout', { method: 'POST', body: {} });
  } finally {
    // Local state is cleared even if the call failed: the user asked to be signed out,
    // and a network error must not leave them looking at their own data.
    setTokens(null, null);
  }
}

/**
 * Kill-switch for every device (F2). Takes no body at all — the bearer token names the
 * account, which is the point: the caller does not hold the other devices' tokens.
 * The server also clears our own refresh cookie, so there is nothing left to resume.
 */
export async function logoutEverywhere(): Promise<void> {
  // Tokens are dropped only after the server confirms, unlike `logout` above. If the call did
  // not go through, the other devices are still signed in — and a tab that threw its own
  // credentials away could not even retry.
  await apiRequest<void>('/v1/auth/logout-all', { method: 'POST' });
  markSignedOut();
  setTokens(null, null);
}

/**
 * Always answers 200, whether or not the address exists — never reveals which addresses
 * are registered, and never reveals throttling either. The UI must therefore show the
 * same confirmation in every case, not "письмо отправлено" only on success.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return apiRequest<void>('/v1/auth/password-reset/request', {
    method: 'POST',
    body: { email: email.trim().toLowerCase() },
  });
}

/** 204 on success; 400 covers invalid, expired and already-used tokens alike. */
export function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  return apiRequest<void>('/v1/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, new_password: newPassword },
  });
}

export function fetchProfile(): Promise<ConsumerProfile> {
  return apiRequest<ConsumerProfile>('/v1/consumer/me');
}

/**
 * Changing the password revokes every OTHER session and keeps the acting one.
 *
 * Which session is "acting" is named by the refresh token — and since ours is in an
 * HttpOnly cookie, we deliberately send NO `refresh_token` field and let the handler
 * read the cookie. Sending nothing while the backend still only looked at the body was
 * exactly the trap docs/27 § 0.2 flagged: the tab performing the change would log
 * itself out.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiRequest<void>('/v1/consumer/me/password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  });
}

export interface DeletionRequest {
  id: string;
  /** `pending` until the manual back-office process completes. */
  status: string;
  requested_at: string;
}

/** 409 `account_deletion_blocked` while an order is still active — surfaced as-is. */
export function requestAccountDeletion(): Promise<DeletionRequest> {
  return apiRequest<DeletionRequest>('/v1/consumer/me/deletion-request', { method: 'POST' });
}

// ---------- подтверждение адреса почты ----------------------------------

export interface EmailVerificationRequested {
  /** Через сколько секунд разрешена следующая отправка — серверное значение, не копия константы. */
  resend_available_in_seconds: number;
  code_ttl_seconds: number;
}

export interface EmailVerificationConfirmed {
  verification_ticket: string;
  /** Нормализованный сервером адрес. Регистрацию слать именно с ним — билет выдан на него. */
  email: string;
  expires_in_seconds: number;
}

/**
 * Первый шаг регистрации: отправить код на адрес.
 *
 * В отличие от `requestPasswordReset`, занятый адрес здесь отвечает 409, а не молчаливым 200.
 * Разница намеренная и не про безопасность: там спрашивают про чужой адрес и утечкой было бы само
 * различие ответов, здесь человек заявляет свой — и «у вас уже есть аккаунт» это то, что ему надо
 * сказать, а не отправить ждать письмо, которого не будет.
 */
export function requestEmailVerification(email: string): Promise<EmailVerificationRequested> {
  return apiRequest<EmailVerificationRequested>('/v1/auth/consumer/email-verification/request', {
    method: 'POST',
    body: { email: email.trim().toLowerCase() },
  });
}

/**
 * Второй шаг: обменять код на билет.
 *
 * Обменивать можно повторно, пока код жив: каждый раз выдаётся новый билет. Это то, что спасает
 * перезагрузку вкладки между вводом кода и отправкой формы.
 */
export function confirmEmailVerification(
  email: string,
  code: string,
): Promise<EmailVerificationConfirmed> {
  return apiRequest<EmailVerificationConfirmed>('/v1/auth/consumer/email-verification/confirm', {
    method: 'POST',
    body: { email: email.trim().toLowerCase(), code: code.trim() },
  });
}

/**
 * Смена адреса в профиле — она же «подтвердить текущий», если прислать тот же адрес.
 * Письмо уходит на НОВЫЙ адрес: доказать надо доступ к тому ящику, куда поедут чеки.
 *
 * Переезд на другой адрес — только с текущим паролем (`current_password`): почта здесь логин и
 * канал сброса пароля, и без пароля открытая вкладка означала бы захват аккаунта. Свой адрес
 * подтверждается без него — сервер спрашивает пароль только когда адрес меняется.
 */
export function requestEmailChange(
  newEmail: string,
  currentPassword?: string,
): Promise<EmailVerificationRequested> {
  return apiRequest<EmailVerificationRequested>('/v1/consumer/me/email', {
    method: 'POST',
    body: {
      new_email: newEmail.trim().toLowerCase(),
      ...(currentPassword !== undefined && currentPassword.length > 0
        ? { current_password: currentPassword }
        : {}),
    },
  });
}

export interface EmailChanged {
  email: string;
  email_verified_at: string;
}

/** Меняет адрес и логин разом. Сессии переживают смену — пароль не менялся. */
export function confirmEmailChange(newEmail: string, code: string): Promise<EmailChanged> {
  return apiRequest<EmailChanged>('/v1/consumer/me/email/confirm', {
    method: 'POST',
    body: { new_email: newEmail.trim().toLowerCase(), code: code.trim() },
  });
}
